import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CompletedInterventionGroupResponse } from '../api/generated';
import { PatientAssignmentControllerApi } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';

interface UseRemovalPreviewParams {
  locationId?: string;
  scenarioId?: string;
  treatmentPlanId?: string;
  patientId?: string;
}

type PreviewStatus = 'loading' | 'loaded' | 'error';

interface UseRemovalPreviewResult {
  // Every group of the plan this patient has completed, whatever scope is currently
  // picked. Callers narrow it themselves, so widening the scope costs no request.
  completedGroups: CompletedInterventionGroupResponse[];
  isLoading: boolean;
  hasError: boolean;
}

/**
 * What removing a patient from a treatment plan would discard, fetched when `isOpen` turns true.
 *
 * Answers from committed state at the moment of asking, which is why it is a request rather than a
 * read of data the page already holds.
 */
export function useRemovalPreview({
  locationId,
  scenarioId,
  treatmentPlanId,
  patientId
}: UseRemovalPreviewParams): UseRemovalPreviewResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [completedGroups, setCompletedGroups] = useState<CompletedInterventionGroupResponse[]>([]);
  // Only ever moved by a callback, never by the effect body: setting state
  // synchronously in an effect cascades renders, and the lint rules forbid it.
  const [status, setStatus] = useState<PreviewStatus>('loading');

  useEffect(() => {
    if (!locationId || !scenarioId || !treatmentPlanId || !patientId) return;

    const controller = new AbortController();
    const api = new PatientAssignmentControllerApi(createApiConfiguration());
    api
      .removalPreview(
        { locationId, scenarioId, treatmentPlanId, patientId },
        { signal: controller.signal }
      )
      .then((response) => {
        setCompletedGroups(response.data);
        setStatus('loaded');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('Failed to load removal preview', error);
        setStatus('error');
        dispatch(showError({ message: t('removal-preview-error') }));
      });

    return () => controller.abort();
  }, [locationId, scenarioId, treatmentPlanId, patientId, dispatch, t]);

  return {
    completedGroups,
    isLoading: status === 'loading',
    hasError: status === 'error'
  };
}
